/**
 * Client economy (M1b) — guest auth + the player's wallet/collection from the server-authoritative
 * economy (the same Worker as online play). Degrades gracefully OFFLINE: if it can't load, `ownedIds()`
 * returns null and the deck builder falls back to the full roster, so solo play is never blocked.
 */
import { serverBase } from './net.ts';

const TOKEN_KEY = 'fc-token';

export interface OwnedCard {
  readonly id: string;
  readonly count: number;
  readonly level: number;
  readonly shards: number;
}
export interface Economy {
  readonly coins: number;
  readonly gems: number;
  readonly cards: readonly OwnedCard[];
}

let token: string | null = null;
let economy: Economy | null = null;
const listeners = new Set<(e: Economy | null) => void>();

/** The economy is HTTP; `serverBase()` is the wss/ws URL — swap the scheme (wss→https, ws→http). */
function httpBase(): string {
  return serverBase().replace(/^ws/, 'http');
}

function notify(): void {
  for (const fn of listeners) fn(economy);
}

/** Reuse a stored guest token, or mint one. Returns null if the server is unreachable. */
async function ensureToken(): Promise<string | null> {
  if (token) return token;
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      token = stored;
      return token;
    }
  } catch {
    /* storage unavailable */
  }
  try {
    const res = await fetch(`${httpBase()}/auth/guest`, { method: 'POST' });
    if (!res.ok) return null;
    const data = (await res.json()) as { token?: string };
    token = data.token ?? null;
    if (token) {
      try {
        localStorage.setItem(TOKEN_KEY, token);
      } catch {
        /* ignore */
      }
    }
    return token;
  } catch {
    return null;
  }
}

/** Fetch wallet + collection (creating a guest identity if needed). Null when offline. */
export async function loadEconomy(): Promise<Economy | null> {
  const t = await ensureToken();
  if (!t) return null;
  try {
    const res = await fetch(`${httpBase()}/economy`, { headers: { Authorization: `Bearer ${t}` } });
    if (!res.ok) return null;
    economy = (await res.json()) as Economy;
    notify();
    return economy;
  } catch {
    return null;
  }
}

export interface PackCard {
  readonly id: string;
  readonly rarity: string;
  readonly isNew: boolean;
}
export interface PackResult {
  readonly cards: readonly PackCard[];
  readonly coins: number;
  readonly gems: number;
}

/** Open a pack (the server rolls + grants). Refreshes the local economy on success. */
export async function openPack(pack = 'standard'): Promise<PackResult | { error: string }> {
  const t = await ensureToken();
  if (!t) return { error: 'offline' };
  try {
    const res = await fetch(`${httpBase()}/economy/open-pack`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}`, 'content-type': 'application/json' },
      body: JSON.stringify({ pack }),
    });
    const data = (await res.json()) as PackResult | { error: string };
    if (res.ok && 'cards' in data) await loadEconomy(); // refresh wallet + collection
    return data;
  } catch {
    return { error: 'offline' };
  }
}

/** Claim a gameplay reward (e.g. a match win). Refreshes the wallet; returns the new coin total. */
export async function grantReward(reason = 'win'): Promise<number | null> {
  const t = await ensureToken();
  if (!t) return null;
  try {
    const res = await fetch(`${httpBase()}/economy/grant`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}`, 'content-type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { coins?: number };
    await loadEconomy();
    return data.coins ?? null;
  } catch {
    return null;
  }
}

export function getEconomy(): Economy | null {
  return economy;
}

/** The current guest token (for authing the match socket), or null if not yet minted. */
export function getToken(): string | null {
  return token;
}

export interface Profile {
  readonly name: string;
  readonly rating: number;
}
let profile: Profile | null = null;

/** Fetch the player's profile (name + rating). Refetch after a ranked match to pick up the new rating. */
export async function loadProfile(): Promise<Profile | null> {
  const t = await ensureToken();
  if (!t) return null;
  try {
    const res = await fetch(`${httpBase()}/me`, { headers: { Authorization: `Bearer ${t}` } });
    if (!res.ok) return null;
    const data = (await res.json()) as { player?: { name?: string; rating?: number } };
    const p = data.player;
    if (!p) return null;
    profile = { name: p.name ?? 'Golfer', rating: p.rating ?? 1000 };
    return profile;
  } catch {
    return null;
  }
}

export function getProfile(): Profile | null {
  return profile;
}

/** The set of owned card ids, or null when the economy is unavailable (⇒ treat all as owned). */
export function ownedIds(): ReadonlySet<string> | null {
  if (economy === null) return null;
  return new Set(economy.cards.map((c) => c.id));
}

export function onEconomyChange(fn: (e: Economy | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
